import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import {
  breakpointSizes,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import FullTransactionsTable from 'src/components/FullTransactionsTable/FullTransactionsTable'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import Subnav, {
  SubnavDesign,
  SubnavOption,
} from 'src/components/Subnav/Subnav'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import VirtualCardRevisionsTable from 'src/components/VirtualCardRevisionsTable/VirtualCardRevisionsTable'
import { ExtendCurrency } from 'src/enums'
import { useWindowScrollBottomHandler } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import { useQueryTransactions } from 'src/queries/transactions.queries'
import { useQueryVirtualCardRevisions } from 'src/queries/virtualCards.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { InfiniteScrollProps } from 'src/types'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

import { TransactionReportType } from '../ReportTransactionsBoxAndLinks/reportTypes'

interface ActivityBoxProps {
  defaultFiltersBody: TransactionsFiltersBody
  showTransactionUser: boolean
  userId: string
  timezone: string
  currency: ExtendCurrency
  vcClearedTransactionAmount?: number
  virtualCard: VirtualCardSecureModel
  clientHeight: number
  hideHistory?: boolean
}

const ActivityBox: React.FC<ActivityBoxProps> = (props): React.ReactElement => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()

  const [isHistoryTab, setIsHistoryTab] = useState(false)
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  const {
    data: transactionsData,
    fetchNextPage: fetchNextTransactionsPage,
    isFetching: transactionsLoading,
    hasNextPage: transactionsHasNextPage,
    complete: transactionsComplete,
  } = useQueryTransactions({
    filters: props.defaultFiltersBody,
    enabled: !isHistoryTab,
    perPage: 20,
  })

  const {
    data: revisionsData,
    fetchNextPage: fetchMoreRevisions,
    isFetching: revisionsLoading,
    hasNextPage: revisionsHasNextPage,
    complete: fullyLoadedRevisions,
  } = useQueryVirtualCardRevisions(
    {
      virtualCardId: props.virtualCard.id,
      queryParams: { limit: 5 },
      enabled: isHistoryTab,
    },
    {
      cardType: props.virtualCard.cardType,
      vendorName: props.virtualCard.billPayMeta?.vendorName,
    },
  )

  const handleScroll = useCallback((): void => {
    if (isHistoryTab && !revisionsLoading && revisionsHasNextPage) {
      fetchMoreRevisions()
    }
  }, [
    isHistoryTab,
    transactionsLoading,
    transactionsHasNextPage,
    fetchNextTransactionsPage,
    revisionsLoading,
    fullyLoadedRevisions,
    fetchMoreRevisions,
  ])

  useWindowScrollBottomHandler(handleScroll)

  const transactionInfiniteScrollProps: InfiniteScrollProps<TransactionsFiltersBody> =
    {
      fetch: () => fetchNextTransactionsPage(),
      loading: transactionsLoading,
      hasNextPage: transactionsHasNextPage,
      complete: transactionsComplete,
    }

  const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
    date: true,
    merchant: true,
    vendor: activeIntegration !== undefined || false,
    card: false,
    cardUser: false,
    cardholderEmail: false,
    expenseCategories: true,
    status: false,
    amount: true,
    currency: false,
    authBillingAmountCents: false,
    authBillingCurrency: false,
    authMerchantAmountCents: false,
    authMerchantCurrency: false,
    authExchangeRate: false,
    attachmentsCount: false,
    creditCardDisplayName: false,
    creditCardType: false,
    receipts: true,
    integration: false,
  }

  const tabOptions: SubnavOption[] = useMemo(() => {
    const options: SubnavOption[] = [
      {
        label: translate('Transactions'),
        onClick: (): void => {
          setIsHistoryTab(false)
        },
        isActive: !isHistoryTab,
        ariaLabelSource: 'Transactions',
      },
    ]
    if (!props.hideHistory) {
      options.push({
        label: translate('History'),
        onClick: (): void => {
          setIsHistoryTab(true)
          analytics?.track(
            'virtual card detail - view virtual card history - tab press',
            {
              screen: '/virtualcards',
              interaction: 'tab press',
              intent: 'view virtual card history',
            },
          )
        },
        isActive: isHistoryTab,
        ariaLabelSource: 'History',
      })
    }
    return options
  }, [props.hideHistory, isHistoryTab, setIsHistoryTab])

  return (
    <Box noPadding>
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            padding: 36px 40px;

            ${getBreakpointOnly(breakpointSizes.XXSM)(css`
              padding: 36px 20px;
            `)}
          `}
        >
          <span
            css={css`
              margin-right: 12px;
              white-space: nowrap;
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Recent Activity')}
          </span>
          {props.vcClearedTransactionAmount !== undefined && (
            <span
              css={css`
                ${getFontCss(
                  20,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate('${transactionAmount} in cleared transactions', {
                transactionAmount: (
                  <Crncy
                    currency={props.currency}
                    amount={toDollars(props.vcClearedTransactionAmount)}
                  />
                ),
              })}
            </span>
          )}
        </div>
        {tabOptions.length > 1 && (
          <Subnav design={SubnavDesign.Box} options={tabOptions} />
        )}
      </div>
      <div
        css={css`
          background-color: ${styleConstants.colors.gray_lightest};
          border-bottom-left-radius: ${styleConstants.borderRadius.box};
          border-bottom-right-radius: ${styleConstants.borderRadius.box};
        `}
      >
        {!isHistoryTab && (
          <FullTransactionsTable
            context={TransactionReportType.Other}
            transactions={transactionsData?.transactions ?? []}
            infiniteScrollProps={transactionInfiniteScrollProps}
            filters={props.defaultFiltersBody}
            fullyLoadedTransactions={transactionsComplete}
            emptyStateDesign={TableEmptyStateDesigns.VirtualCardTransactions}
            design={TransactionsTableLargeDesigns.Base}
            vcRefFields
            preventBulkActions
            defaultColumnVisibilityModel={defaultColumnVisibilityModel}
            clientHeight={props.clientHeight}
          />
        )}
        {isHistoryTab && (
          <VirtualCardRevisionsTable
            userId={props.userId}
            timezone={props.timezone}
            revisions={revisionsData?.revisions ?? []}
            currency={props.currency}
          />
        )}
      </div>
      {isHistoryTab && revisionsLoading && <LoadingBoxBottom />}
    </Box>
  )
}

export default ActivityBox
